import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import { getSecuritySettingsConfig } from '../../../api/firebase/firestore/security/read';
import { updateSecuritySaml } from '../../../api/firebase/function/security/securitySamlFunction';
import type { SamlIdp, SamlMode } from '../../../constants/enum';
import {
  samlIdp,
  samlIdpLabel,
  samlMode,
  samlModeLabel,
  SAML_CERT_MAX_LENGTH,
} from '../../../constants/enum';
import {
  gray40,
  gray80,
  gray100,
  green50,
  primaryButtonBackgroundColor,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import {
  dialogText,
  dialogTitle,
  regularMd,
  regularSm,
  regularXs,
} from '../../../constants/location';
import CustomTextfield from '../../uiParts/form/CustomTextfield';
import CustomSelectField from '../../uiParts/form/CustomSelectField';

interface Props {
  /** 代理モード / 運営時の対象企業。companyAdmin 自身は未指定でよい */
  companyId?: string;
}

const styles = {
  // .App のグローバル text-align: center を打ち消す（本文は左揃え基本・design-rules §1）
  wrapper: {
    textAlign: 'left' as const,
  },
  description: {
    ...regularSm,
    color: gray100,
    marginBottom: '8px',
  },
  caption: {
    ...regularXs,
    color: gray80,
    marginBottom: '16px',
  },
  errorText: {
    ...regularSm,
    color: red50,
    marginBottom: '12px',
  },
  infoText: {
    ...regularSm,
    color: green50,
    marginBottom: '12px',
  },
  sectionTitle: {
    ...regularMd,
    color: gray100,
    fontWeight: 600,
    margin: '24px 0 8px',
  },
  spInfoBox: {
    backgroundColor: gray40,
    borderRadius: '4px',
    padding: '12px 16px',
    marginBottom: '16px',
  },
  spInfoLabel: {
    ...regularXs,
    color: gray80,
  },
  spInfoValue: {
    ...regularSm,
    color: gray100,
    marginBottom: '8px',
    wordBreak: 'break-all' as const,
  },
  formRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '16px',
    marginBottom: '8px',
  },
  formItem: {
    minWidth: '280px',
    flex: '1 1 280px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
  },
  actionButton: {
    height: '44px',
    minWidth: '140px',
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  dialogBody: {
    ...dialogText,
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
};

const enabledOptions = [
  { option: '有効', value: 'enabled' },
  { option: '無効', value: 'disabled' },
];

const idpOptions = Object.entries(samlIdpLabel).map(([value, label]) => ({
  option: label,
  value,
}));

const modeOptions = Object.entries(samlModeLabel).map(([value, label]) => ({
  option: label,
  value,
}));

/** SP（Gen Drive 側）情報。functions 側 spInfoFor と同じ導出式（プロジェクト ID 基点） */
const spInfo = (companyId: string): { entityId: string; acsUrl: string } => {
  const domain = `${process.env.REACT_APP_GCP_PROJECT_ID}.firebaseapp.com`;
  return {
    entityId: `https://${domain}/saml/gd-${companyId}`,
    acsUrl: `https://${domain}/__/auth/handler`,
  };
};

/**
 * 設定 > セキュリティ > SAML SSO タブ（#2983）。
 * IdP（Microsoft Entra ID / Google Workspace）のメタデータ登録・対象ドメイン・
 * ログインモード（SAML 必須 / 従来ログイン併用可）を設定する。
 * 保存すると GCIP に企業専用の SAML プロバイダが作成/更新される。
 */
const SamlTab: React.FC<Props> = ({ companyId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [infoText, setInfoText] = useState('');
  const [configured, setConfigured] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [idp, setIdp] = useState<SamlIdp>(samlIdp.entra);
  const [domain, setDomain] = useState('');
  const [mode, setMode] = useState<SamlMode>(samlMode.optional);
  const [idpEntityId, setIdpEntityId] = useState('');
  const [ssoUrl, setSsoUrl] = useState('');
  const [certificate, setCertificate] = useState('');

  const load = useCallback(async (): Promise<void> => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorText('');
    try {
      const config = await getSecuritySettingsConfig(companyId);
      const saml = config?.saml;
      if (saml) {
        setConfigured(true);
        setEnabled(saml.enabled);
        setIdp(saml.idp);
        setDomain(saml.domain);
        setMode(saml.mode);
        setIdpEntityId(saml.idp_entity_id ?? '');
        setSsoUrl(saml.sso_url ?? '');
      }
    } catch (e) {
      Sentry.captureException(e);
      setErrorText(e instanceof Error ? e.message : 'SAML 設定の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const validate = (): string => {
    if (!domain.trim()) return '対象ドメインを入力してください。';
    if (!idpEntityId.trim()) return 'IdP エンティティ ID を入力してください。';
    if (!ssoUrl.trim().startsWith('https://'))
      return 'SSO URL は https:// で始まる URL を入力してください。';
    if (!configured && !certificate.trim())
      return '初回登録時は署名証明書（PEM 形式）を貼り付けてください。';
    if (certificate.length > SAML_CERT_MAX_LENGTH) return '署名証明書が長すぎます。';
    return '';
  };

  const handleSaveClick = (): void => {
    setInfoText('');
    const validationError = validate();
    if (validationError) {
      setErrorText(validationError);
      return;
    }
    setErrorText('');
    // SAML 必須で有効化する場合は、ロックアウトの注意を確認してから保存する
    if (enabled && mode === samlMode.required) {
      setConfirmOpen(true);
      return;
    }
    void handleSave();
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setErrorText('');
    try {
      await updateSecuritySaml({
        company_id: companyId,
        enabled,
        idp,
        domain: domain.trim(),
        mode,
        idp_entity_id: idpEntityId.trim(),
        sso_url: ssoUrl.trim(),
        certificate: certificate.trim() || undefined,
      });
      setConfigured(true);
      setCertificate('');
      setInfoText('SAML 設定を保存しました。');
      setConfirmOpen(false);
    } catch (e) {
      Sentry.captureException(e);
      setErrorText(e instanceof Error ? e.message : 'SAML 設定の保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const sp = companyId ? spInfo(companyId) : null;

  return (
    <Box sx={styles.wrapper}>
      <Typography sx={styles.description}>
        Microsoft Entra ID / Google Workspace の社内アカウントで Gen Drive
        にシングルサインオン（SAML SSO）できるようにします。
      </Typography>
      <Typography sx={styles.caption}>
        IdP 側のアプリ登録には下記の「SP 情報」を使用します。「SAML
        必須」にすると、対象ドメインのメンバーはメール/パスワードでログインできなくなります（管理者もSSO経由になります）。
      </Typography>

      {errorText && <Typography sx={styles.errorText}>{errorText}</Typography>}
      {infoText && <Typography sx={styles.infoText}>{infoText}</Typography>}

      {sp && (
        <Box sx={styles.spInfoBox}>
          <Typography sx={styles.spInfoLabel}>
            SP エンティティ ID（IdP 側の「識別子 / Entity ID」に設定）
          </Typography>
          <Typography sx={styles.spInfoValue}>{sp.entityId}</Typography>
          <Typography sx={styles.spInfoLabel}>
            ACS URL（IdP 側の「応答 URL / ACS URL」に設定）
          </Typography>
          <Typography sx={[styles.spInfoValue, { marginBottom: 0 }]}>{sp.acsUrl}</Typography>
        </Box>
      )}

      <Typography sx={styles.sectionTitle}>IdP 設定</Typography>
      <Box sx={styles.formRow}>
        <Box sx={styles.formItem}>
          <CustomSelectField
            labelText="IdP（アイデンティティプロバイダ）"
            list={idpOptions}
            value={idp}
            onChange={(e): void => setIdp(e.target.value as SamlIdp)}
            width="100%"
            height="44px"
            formMb="0"
          />
        </Box>
        <Box sx={styles.formItem}>
          <CustomTextfield
            labelText="対象ドメイン"
            value={domain}
            onChange={(e): void => setDomain(e.target.value)}
            placeholder="example.co.jp"
            inputWidth="100%"
            height="44px"
            fontSizeIs14px
            formMb="0"
          />
        </Box>
      </Box>
      <Box sx={styles.formRow}>
        <Box sx={styles.formItem}>
          <CustomTextfield
            labelText="IdP エンティティ ID"
            value={idpEntityId}
            onChange={(e): void => setIdpEntityId(e.target.value)}
            inputWidth="100%"
            height="44px"
            fontSizeIs14px
            formMb="0"
          />
        </Box>
        <Box sx={styles.formItem}>
          <CustomTextfield
            labelText="SSO URL（ログイン URL）"
            value={ssoUrl}
            onChange={(e): void => setSsoUrl(e.target.value)}
            inputWidth="100%"
            height="44px"
            fontSizeIs14px
            formMb="0"
          />
        </Box>
      </Box>
      <Box sx={styles.formRow}>
        <Box sx={{ width: '100%' }}>
          <CustomTextfield
            labelText={
              configured ? '署名証明書（PEM 形式・変更時のみ貼り付け）' : '署名証明書（PEM 形式）'
            }
            value={certificate}
            onChange={(e): void => setCertificate(e.target.value)}
            placeholder="-----BEGIN CERTIFICATE-----"
            inputWidth="100%"
            fontSizeIs14px
            formMb="0"
            multiline
            rows={4}
          />
        </Box>
      </Box>

      <Typography sx={styles.sectionTitle}>ログイン設定</Typography>
      <Box sx={styles.formRow}>
        <Box sx={styles.formItem}>
          <CustomSelectField
            labelText="ログインモード"
            list={modeOptions}
            value={mode}
            onChange={(e): void => setMode(e.target.value as SamlMode)}
            width="100%"
            height="44px"
            formMb="0"
          />
        </Box>
        <Box sx={styles.formItem}>
          <CustomSelectField
            labelText="SSO の状態"
            list={enabledOptions}
            value={enabled ? 'enabled' : 'disabled'}
            onChange={(e): void => setEnabled(e.target.value === 'enabled')}
            width="100%"
            height="44px"
            formMb="0"
          />
        </Box>
      </Box>

      <Box sx={styles.actions}>
        <Button
          onClick={handleSaveClick}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={[primaryButtonBackgroundColor, styles.actionButton]}
        >
          保存
        </Button>
      </Box>

      <Dialog open={confirmOpen} onClose={(): void => setConfirmOpen(false)} sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitle}>SAML 必須モードで保存しますか？</Typography>
          <IconButton onClick={(): void => setConfirmOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography sx={styles.dialogBody}>
          SAML
          必須にすると、対象ドメインの全メンバー（管理者を含む）はメール/パスワードでログインできなくなります。
          IdP の設定に誤りがあるとログイン不能になるおそれがあります。事前に「従来ログイン併用可」で
          SSO ログインの動作確認を行うことを推奨します。
        </Typography>
        <Box sx={styles.dialogActions}>
          <Button
            onClick={(): void => setConfirmOpen(false)}
            sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
          >
            キャンセル
          </Button>
          <Button
            onClick={(): void => void handleSave()}
            disabled={saving}
            sx={[primaryButtonBackgroundColor, styles.actionButton]}
          >
            保存する
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default SamlTab;
